import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AccessRequestStatus } from '@igf-minar/shared'
import { api } from '../lib/api'
import { formatDateLong } from '../lib/dates'
import { useFeatureFlags } from '../lib/features'
import { NotFound } from '../components/NotFound'
import { useMessages } from '@/i18n/messages'
import { umsoknStadfestingMessages } from '@/i18n/text/umsoknStadfesting'

export const Route = createFileRoute('/umsokn/stadfesting')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: ApprovalPage,
})

type ApprovalMessages = (typeof umsoknStadfestingMessages)['is']

function statusLabel(m: ApprovalMessages, status: AccessRequestStatus): string {
  const labels: Record<AccessRequestStatus, string> = {
    pending: m.statusPending,
    approved: m.statusApproved,
    rejected: m.statusRejected,
    completed: m.statusCompleted,
    cancelled: m.statusCancelled,
    expired: m.statusExpired,
  }
  return labels[status] ?? status
}

function ApprovalPage() {
  const m = useMessages(umsoknStadfestingMessages)
  const flags = useFeatureFlags()
  const { token } = Route.useSearch()
  const [decided, setDecided] = useState<'approve' | 'reject' | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['access-request-approval', token],
    queryFn: () => api.accessRequests.getApproval(token),
    enabled: token !== '' && flags.accessRequests,
    retry: false,
  })

  async function decide(decision: 'approve' | 'reject') {
    setError(null)
    setLoading(true)
    try {
      await api.accessRequests.decide(token, decision, note.trim() || undefined)
      setDecided(decision)
    } catch (err) {
      setError((err as Error).message || m.decideFailed)
    } finally {
      setLoading(false)
    }
  }

  if (flags.isLoading) return null
  if (!flags.accessRequests) return <NotFound />
  if (token === '') return <Shell><p className="text-sm text-slate-600">{m.missingToken}</p></Shell>
  if (isLoading) return <Shell><p className="text-sm text-slate-500">{m.loading}</p></Shell>
  if (loadError || !data) {
    return (
      <Shell>
        <p className="text-sm text-slate-600">
          {m.invalidLink}
        </p>
      </Shell>
    )
  }

  const r = data.request
  const company = r.companyName || m.companyFallback(r.companyKennitala)

  if (decided) {
    return (
      <Shell>
        <div
          className={`rounded-lg border p-5 text-sm ${
            decided === 'approve'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-slate-200 bg-slate-50 text-slate-700'
          }`}
        >
          {decided === 'approve' ? (
            <>
              <p className="font-medium">{m.approvedTitle}</p>
              <p className="mt-2">{m.approvedBody(r.applicantName)}</p>
            </>
          ) : (
            <>
              <p className="font-medium">{m.rejectedTitle}</p>
              <p className="mt-2">{m.rejectedBody}</p>
            </>
          )}
        </div>
      </Shell>
    )
  }

  if (r.status !== 'pending') {
    return (
      <Shell>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
          <p className="font-medium">{statusLabel(m, r.status)}</p>
          <p className="mt-2">
            {r.status === 'expired'
              ? m.expired
              : m.alreadyHandled}
          </p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <p className="text-sm text-slate-600">
        <strong>{r.applicantName}</strong> {m.requestMiddle}{' '}
        <strong>{company}</strong>{m.requestEnd}
      </p>

      <dl className="mt-6 divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
        <Row label={m.rowCompany} value={company} />
        <Row label={m.rowKennitala} value={r.companyKennitala} />
        <Row label={m.rowApplicant} value={r.applicantName} />
        <Row label={m.rowEmail} value={r.applicantEmail} />
        <Row label={m.rowPhone} value={r.applicantPhone} />
        <Row label={m.rowApplied} value={formatDateLong(r.createdAt)} />
      </dl>

      {r.selfApproved && (
        <p className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {m.selfApproved}
        </p>
      )}

      <label className="mt-6 block text-sm font-medium text-slate-700">
        {m.comment} <span className="font-normal text-slate-400">{m.optional}</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="mt-1 w-full resize-y rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
        />
      </label>

      {error && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          disabled={loading}
          onClick={() => decide('approve')}
          className="flex-1 rounded bg-emerald-600 px-4 py-2 font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
        >
          {m.approve}
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={() => decide('reject')}
          className="flex-1 rounded border border-slate-300 px-4 py-2 font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          {m.reject}
        </button>
      </div>

      <p className="mt-4 text-xs text-slate-400">
        {m.footnote}
      </p>
    </Shell>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 px-4 py-2.5">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-900">{value}</dd>
    </div>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  const m = useMessages(umsoknStadfestingMessages)
  return (
    <div className="max-w-xl mx-auto px-6 py-16">
      <h1 className="text-2xl font-bold text-slate-900">{m.title}</h1>
      <div className="mt-6">{children}</div>
      <Link to="/login" className="mt-8 inline-block text-sm text-emerald-700 underline hover:text-emerald-900">
        {m.toLogin}
      </Link>
    </div>
  )
}
